import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/api/client";
import { qk } from "@/api/keys";
import { useAuthStore } from "@/stores/authStore";

export interface StatsResponse {
  total_answered: number;
  total_correct: number;
  accuracy: number;
  current_streak: number;
  longest_streak: number;
  last_study_date: string | null;
  srs_due_count: number;
  weakness_count: number;
  entitlement_count: number;
}

export interface MasteryItem {
  grammar_point_key: string;
  title: string;
  score: number;
}

export interface MasteryResponse {
  items: MasteryItem[];
}

export interface WeaknessOut {
  weakness_id: string;
  question_id: string;
  question_prompt_zh: string;
  error_tag_key: string;
  error_tag_label: string;
  grammar_point_key: string;
  grammar_point_label: string;
  group_member_count: number;
  review_group_key: string;
  review_group_label: string;
  review_group_member_count: number;
  review_group_grammar_keys: string[];
  user_answer: string;
  correct_answer: string;
  resolved: boolean;
  created_at: string;
}

export interface WeaknessListResponse {
  items: WeaknessOut[];
}

export interface LearningPreferencesResponse {
  current_pack_id: string | null;
}

export interface SrsDueItem {
  grammar_point_key: string;
  level: number;
  due_at: string;
}

export interface SrsDueResponse {
  items: SrsDueItem[];
}

function useTokenGuardedQuery<T>(
  queryKey: readonly unknown[],
  path: string,
) {
  const token = useAuthStore((s) => s.token);
  return useQuery({
    queryKey,
    queryFn: () => apiGet<T>(path),
    enabled: Boolean(token),
  });
}

export function useStats() {
  return useTokenGuardedQuery<StatsResponse>(qk.stats(), "me/stats");
}

export function useMastery() {
  return useTokenGuardedQuery<MasteryResponse>(qk.mastery(), "me/mastery");
}

export function useWeaknesses() {
  return useTokenGuardedQuery<WeaknessListResponse>(
    qk.weaknesses(),
    "me/weaknesses",
  );
}

export function useLearningPreferences() {
  return useTokenGuardedQuery<LearningPreferencesResponse>(
    qk.learningPreferences(),
    "me/learning-preferences",
  );
}

export function useSrsDue() {
  return useTokenGuardedQuery<SrsDueResponse>(qk.srsDue(), "me/srs/due");
}
